// Projekt-Karte panel — the clickable button-tree that replaces the free-text
// "Wo im Code gehört das hin?" question in the create flow when a project has a
// map. A non-technical author drills down (≤6 buttons per level), sees a
// breadcrumb + "Zurück", and either lands on a leaf (→ a self-labelled,
// quotable location answer + invisible advisory code-paths — the paths are
// NEVER rendered to the author; they are technical payload, not UI) or taps the fixed
// "Ich bin nicht sicher — im Chat beschreiben" escape hatch on ANY level (→ the
// free-text fallback, with the partially-clicked path riding along as context).
//
// This is a faithful port of the vanilla prototype renderer onto the app's
// design tokens. The tree walk is pure and shared (`@loom/shared/code-map`);
// the panel slices to the ceiling defensively (the ≤6 rule is a data property).

import { useState } from "react";

import {
  childrenOf,
  MAX_CHILDREN_PER_LEVEL,
  nodeById,
  nodeDisplayDescription,
  nodeDisplayLabel,
  pathDisplayLabels,
  pathLabels,
  type CodeMapTree,
} from "@loom/shared/code-map";

import { useLocale, useT } from "../i18n/index.js";

export interface ProjektKartePanelProps {
  readonly tree: CodeMapTree;
  /**
   * A leaf was confirmed: the canonical label path (the answer bytes), its
   * advisory code paths, and the NODE IDS that were clicked — the last so the
   * caller can re-derive display labels at the viewer's locale and re-open the
   * panel where the pick was made.
   */
  readonly onLeaf: (
    labelPath: string[],
    codePaths: string[],
    nodePath: string[],
  ) => void;
  /**
   * The escape hatch: fall back to free text, carrying the partial path the
   * user had drilled into — canonical (grounding-gate bytes, interpolated
   * into the create seed) and display (the viewer's own locale) both, since
   * this single path is BOTH machine-facing and shown back to the user. A
   * lone canonical string here printed the map's language ("Zirkular-Tool ›
   * Minen …") under an English viewer's own escape-hatch box, for nodes the
   * picker had just shown them in English — sibling of the same defect
   * `onLeaf`'s `labelPath` avoids by riding node ids back for a display
   * re-derive; a partial-path skip has no confirmed leaf to re-derive from,
   * so it must carry both strings itself.
   */
  readonly onSkip: (
    partialLabelPath: string[],
    partialDisplayPath: string[],
  ) => void;
  /**
   * Where to OPEN the drill (node ids). Coming Back from step 2 used to reset the
   * map to the root, so the user's own confirmed pick was gone from the screen
   * and they had to re-drill from scratch to see (or change) it. Passing the
   * stored path re-opens the panel on that node — a leaf re-opens on its confirm
   * card, which is exactly where the answer was made.
   *
   * Ids that no longer resolve in `tree` are dropped rather than rendering an
   * empty level: the map is regenerated server-side and a stale draft must
   * degrade to the root, not to a dead end.
   */
  readonly initialPath?: readonly string[];
}

export function ProjektKartePanel({
  tree,
  onLeaf,
  onSkip,
  initialPath,
}: ProjektKartePanelProps) {
  const t = useT();
  const { locale } = useLocale();
  // The drill path as node ids; empty = at the roots.
  const [path, setPath] = useState<string[]>(() =>
    (initialPath ?? []).filter((id) => nodeById(tree, id) !== undefined),
  );

  const parentId = path.length ? (path[path.length - 1] as string) : null;
  const current = parentId !== null ? nodeById(tree, parentId) : undefined;
  const kids = childrenOf(tree, parentId).slice(0, MAX_CHILDREN_PER_LEVEL);
  const atLeaf = current !== undefined && kids.length === 0;
  // Two parallel arrays on purpose: `labels` is the canonical answer text the
  // grounding gate scans for and rides out through onLeaf/onSkip untouched;
  // `shownLabels` is what the viewer reads in their own locale.
  const labels = pathLabels(tree, path);
  const shownLabels = pathDisplayLabels(tree, path, locale);

  const goBack = (): void => setPath((p) => p.slice(0, -1));
  const skip = (): void => onSkip(labels, shownLabels);

  return (
    <div className="pk-panel">
      <div className="pk-crumbs">
        <button
          type="button"
          className="pk-back"
          onClick={goBack}
          disabled={path.length === 0}
        >
          ← {t("focus.create.location.map.back")}
        </button>
        <span className={"pk-crumb" + (path.length === 0 ? " is-here" : "")}>
          {t("focus.create.location.map.crumbRoot")}
        </span>
        {shownLabels.map((label, i) => (
          <span key={`crumb-${i}`} className="pk-crumb-group">
            <span className="pk-crumb-sep" aria-hidden>
              ›
            </span>
            <span
              className={
                "pk-crumb" + (i === shownLabels.length - 1 ? " is-here" : "")
              }
            >
              {label}
            </span>
          </span>
        ))}
      </div>

      {atLeaf && current ? (
        // R2-B: the path used to be printed here too — the confirm card's own
        // bolded, own-wrapping restatement of exactly what the crumb bar above
        // (`.pk-crumbs`, `is-here` on the last segment) already says. The
        // card's job is to confirm the pick, not re-state it.
        <div className="pk-leaf">
          {nodeDisplayDescription(current, locale) !== "" && (
            <div className="pk-leaf-desc">
              {nodeDisplayDescription(current, locale)}
            </div>
          )}
          <div className="pk-actions">
            <button
              type="button"
              className="focus-btn"
              onClick={() => onLeaf(labels, [...current.code_paths], [...path])}
            >
              {t("focus.create.location.map.confirm")}
            </button>
            <button type="button" className="pk-skip" onClick={skip}>
              {t("focus.create.location.map.skip")}
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="pk-grid">
            {kids.map((node) => {
              const subCount = childrenOf(tree, node.id).length;
              return (
                <button
                  key={node.id}
                  type="button"
                  className="pk-btn"
                  onClick={() => setPath((p) => [...p, node.id])}
                >
                  <span className="pk-btn-head">
                    <span className="pk-btn-label">
                      {nodeDisplayLabel(node, locale)}
                    </span>
                    <span className="pk-btn-count">
                      {subCount > 0
                        ? t("focus.create.location.map.areas", {
                            count: subCount,
                          })
                        : t("focus.create.location.map.choose")}
                    </span>
                  </span>
                  {nodeDisplayDescription(node, locale) !== "" && (
                    <span className="pk-btn-desc">
                      {nodeDisplayDescription(node, locale)}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
          <button type="button" className="pk-skip" onClick={skip}>
            {t("focus.create.location.map.skip")}
          </button>
        </>
      )}
    </div>
  );
}
